import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Quote } from 'lucide-react';
import { useI18n } from '@/lib/i18n';

const testimonials = [
  {
    quote: {
      en: "AgriTrack transformed how we manage our 2,000-head operation. Feed costs dropped 12% in the first quarter alone.",
      tr: "AgriTrack 2.000 başlık operasyonumuzu yönetme şeklimizi dönüştürdü. Yem maliyetleri sadece ilk çeyrekte %12 düştü."
    },
    author: "Mehmet Yılmaz",
    role: {
      en: "Operations Manager",
      tr: "Operasyon Müdürü"
    },
    farm: "Yılmaz Çiftliği",
  },
  {
    quote: {
      en: "Finally, software that understands the realities of farming. The offline capability is a game-changer for our remote paddocks.",
      tr: "Sonunda çiftçiliğin gerçeklerini anlayan bir yazılım. Çevrimdışı özellik uzak meralarımız için oyun değiştirici."
    },
    author: "Ayşe Demir",
    role: {
      en: "Farm Owner",
      tr: "Çiftlik Sahibi"
    },
    farm: "Demir Hayvancılık",
  },
  {
    quote: {
      en: "The growth analytics helped us identify which genetics perform best in our conditions. ROI was visible within months.",
      tr: "Büyüme analitiği, koşullarımızda hangi genetiğin en iyi performansı gösterdiğini belirlememize yardımcı oldu. ROI aylar içinde görünür oldu."
    },
    author: "Ali Kaya",
    role: {
      en: "Livestock Director",
      tr: "Hayvancılık Direktörü"
    },
    farm: "Kaya Tarım A.Ş.",
  },
];

export const Testimonials = () => {
  const { t, locale } = useI18n();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      ref={ref}
      className="py-24 lg:py-32 bg-white"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 lg:mb-16"
        >
          <h2
            className="text-3xl sm:text-4xl font-black text-primary tracking-tight"
            data-testid="testimonials-title"
          >
            {t('testimonials.title')}
          </h2>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            {t('testimonials.subtitle')}
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.author}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 + index * 0.1, duration: 0.5 }}
              className="bg-surface p-8 rounded-2xl border border-primary/5 hover:shadow-soft transition-shadow duration-300"
              data-testid={`testimonial-${index}`}
            >
              <Quote className="w-8 h-8 text-forest-300 mb-4" />
              <p className="text-primary/90 leading-relaxed mb-6">
                "{testimonial.quote[locale]}"
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-forest-100 rounded-full flex items-center justify-center">
                  <span className="text-forest-700 font-bold text-sm">
                    {testimonial.author.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-primary text-sm">
                    {testimonial.author}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {testimonial.role[locale]} · {testimonial.farm}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
